// 22-px context strip showing the entire contract span. Mini status-coloured
// dots, an outlined active-window indicator, today line + label, and a foot
// meta row (start · today % elapsed · end). The active-window indicator is
// pointer-draggable and keyboard-arrow-driveable so the user can scrub the
// detailed window across the contract span without leaving the bar.

import { clsx } from "clsx";
import { useCallback, useRef } from "react";
import type { Milestone } from "../../../../types";
import { deriveMilestoneDisplayStatus } from "../../../../utils/deriveMilestoneDisplayStatus";
import { deriveTimelineDotLifecycle } from "../../../../utils/deriveMilestoneDisplayLifecycle";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { effectiveTimelineDate, positionFraction } from "./timelineGeometry";
import { dotClass } from "./timelineDotClass";

export interface TimelineContextBarProps {
  contractStart: string;
  contractEnd: string;
  today: string;
  milestones: readonly Milestone[];
  /** Active window's [start, end] dates from the parent. Drives the outlined
   *  rectangle indicating where the detailed window sits. */
  windowStart: string;
  windowEnd: string;
  /** Optional handler called on pointer drag or arrow keys with a new ISO
   *  centre date for the active window. Parent (MilestoneTab) wires this
   *  to the timelineFocus URL param. When omitted the indicator renders
   *  as a static visual. */
  onWindowScrub?: (centreIso: string) => void;
}

const ISO_DAY_MS = 86_400_000;

function isoDayUTC(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function fractionToIso(
  fraction: number,
  contractStart: string,
  contractEnd: string,
): string {
  const startMs = isoDayUTC(contractStart);
  const endMs = isoDayUTC(contractEnd);
  const clamped = Math.max(0, Math.min(1, fraction));
  const targetMs = Math.round(startMs + clamped * (endMs - startMs));
  const d = new Date(targetMs);
  const yyyy = d.getUTCFullYear().toString().padStart(4, "0");
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const dd = d.getUTCDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function shiftIso(iso: string, days: number): string {
  const ms = isoDayUTC(iso) + days * ISO_DAY_MS;
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear().toString().padStart(4, "0");
  const mm = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const dd = d.getUTCDate().toString().padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function clampIso(iso: string, lo: string, hi: string): string {
  if (iso < lo) return lo;
  if (iso > hi) return hi;
  return iso;
}

export default function TimelineContextBar({
  contractStart,
  contractEnd,
  today,
  milestones,
  windowStart,
  windowEnd,
  onWindowScrub,
}: TimelineContextBarProps) {
  const todayFraction = positionFraction(today, contractStart, contractEnd);
  const winStartFraction = positionFraction(windowStart, contractStart, contractEnd);
  const winEndFraction = positionFraction(windowEnd, contractStart, contractEnd);
  const elapsedPct = Math.round(todayFraction * 100);
  const summary = `Contract progress: ${elapsedPct}% elapsed, ${milestones.length} milestones`;
  const trackRef = useRef<HTMLDivElement | null>(null);

  const updateFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track || !onWindowScrub) return;
      const rect = track.getBoundingClientRect();
      if (rect.width <= 0) return;
      const fraction = (clientX - rect.left) / rect.width;
      const iso = clampIso(
        fractionToIso(fraction, contractStart, contractEnd),
        contractStart,
        contractEnd,
      );
      onWindowScrub(iso);
    },
    [contractStart, contractEnd, onWindowScrub],
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!onWindowScrub) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!onWindowScrub) return;
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    updateFromClientX(e.clientX);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!onWindowScrub) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const currentCentreIso = (() => {
    const startMs = isoDayUTC(windowStart);
    const endMs = isoDayUTC(windowEnd);
    const ms = Math.round((startMs + endMs) / 2);
    const d = new Date(ms);
    const yyyy = d.getUTCFullYear().toString().padStart(4, "0");
    const mm = (d.getUTCMonth() + 1).toString().padStart(2, "0");
    const dd = d.getUTCDate().toString().padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  })();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!onWindowScrub) return;
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const step = e.shiftKey ? 7 : 1;
    const delta = e.key === "ArrowLeft" ? -step : step;
    const next = clampIso(
      shiftIso(currentCentreIso, delta),
      contractStart,
      contractEnd,
    );
    onWindowScrub(next);
  };

  return (
    <section
      data-testid="timeline-context-bar"
      aria-label={summary}
      className="space-y-1.5"
    >
      <div
        ref={trackRef}
        className="relative h-[22px] rounded-md bg-slate-100 dark:bg-slate-700/40"
      >
        {/* Elapsed shading */}
        <div
          aria-hidden="true"
          data-testid="context-bar-elapsed"
          className="absolute inset-y-0 left-0 rounded-l-md bg-brand-100/60 dark:bg-brand-900/30"
          style={{ width: `${(todayFraction * 100).toFixed(2)}%` }}
        />
        {/* Active-window indicator. When onWindowScrub is supplied, this is
            a focusable button that drags / arrow-keys to move the centre. */}
        {onWindowScrub ? (
          <button
            type="button"
            data-testid="context-bar-window"
            aria-label={`Active window indicator from ${windowStart} to ${windowEnd}. Drag or arrow keys to scrub.`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onKeyDown={handleKeyDown}
            style={{
              left: `${(winStartFraction * 100).toFixed(2)}%`,
              width: `${((winEndFraction - winStartFraction) * 100).toFixed(2)}%`,
              touchAction: "none",
            }}
            className="absolute inset-y-0 cursor-ew-resize rounded-md border-2 border-brand-500/80 bg-transparent focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-brand-400/70"
          />
        ) : (
          <div
            aria-hidden="true"
            data-testid="context-bar-window"
            className="absolute inset-y-0 rounded-md border-2 border-brand-500/80 dark:border-brand-400/70"
            style={{
              left: `${(winStartFraction * 100).toFixed(2)}%`,
              width: `${((winEndFraction - winStartFraction) * 100).toFixed(2)}%`,
            }}
          />
        )}
        {/* Today vertical line */}
        <div
          aria-hidden="true"
          data-testid="context-bar-today"
          className="absolute top-0 bottom-0 w-px -translate-x-1/2 bg-slate-700 dark:bg-slate-200"
          style={{ left: `${(todayFraction * 100).toFixed(2)}%` }}
        />
        {/* Mini milestone dots */}
        {milestones.map((m) => {
          const positionIso = effectiveTimelineDate(m);
          if (!positionIso) return null;
          const display = deriveMilestoneDisplayStatus(m, today);
          // Override lifecycle for dot colour — partial+paid renders split gradient.
          const dotDisplay = { ...display, lifecycle: deriveTimelineDotLifecycle(m) };
          const fraction = positionFraction(
            positionIso,
            contractStart,
            contractEnd,
          );
          return (
            <span
              key={m.id}
              aria-hidden="true"
              data-testid={`context-bar-dot-${m.id}`}
              className={clsx(
                "absolute top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full",
                dotClass(dotDisplay),
              )}
              style={{ left: `${(fraction * 100).toFixed(2)}%` }}
            />
          );
        })}
      </div>
      <div className="flex items-center justify-between text-caption text-slate-500 dark:text-slate-400">
        <span>{formatDayMonthYear(contractStart)}</span>
        <span>today · {elapsedPct}% elapsed</span>
        <span>{formatDayMonthYear(contractEnd)}</span>
      </div>
    </section>
  );
}
